/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag } from '@fortawesome/free-solid-svg-icons';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import reportInvoiceService from 'service/reportInvoice';
import Button from 'components/base/Button';
import PurchaseByInvoiceModalPriceList from './modal-price-list';
import legalEntitySettingsService from 'service/legalEntitySettings';
import ExcelIcon from './filter/ExcelIcon';
import { fieldMappingInvoice } from 'data/fieldMaping';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import { pageToOffset } from 'utils/pageToOffset';

export type PurchaseData = {
  invoice: string;
  invoice_date: string;
  supplier: string;
  warehouse: string;
  currency: string;
  type: string;
  payment_procedure: string;
  creation_date: string;
  purchase_date: string;
  guid: string;
  gupid: number;
};

interface PurchaseByInvoiceTableProps {
  dateFrom?: any;
  dateTo?: any;
  paymentId?: any;
  optionSenderId?: any;
  warehouseId?: any;
  currencyId?: any;
  typeId?: any;
  currentPage?: any;
  setCurrentPage?: any;
  filialId?: any;
  setLabeledVisibleFields?: any;
}

const PurchaseByInvoiceTable = ({
  dateFrom,
  dateTo,
  paymentId,
  optionSenderId,
  currencyId,
  typeId,
  warehouseId,
  currentPage,
  setCurrentPage,
  filialId,
  setLabeledVisibleFields
}: PurchaseByInvoiceTableProps) => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageCount, setPageCount] = useState(1);

  const url = process.env.REACT_APP_CDN_URL;

  const navigate = useNavigate();

  const [modalPriceList, setModalPriceList] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');

  // Memoized onVisibleFieldsChange callback with shallow comparison
  const handleVisibleFieldsChange = useCallback(
    (visibleFields: { [key: string]: { hidden: boolean; order: number } }) => {
      const newLabeledFields = Object.keys(visibleFields)
        .filter(columnKey => !visibleFields[columnKey].hidden) // Only include non-hidden fields
        .map(columnKey => {
          const backendField = Object.keys(fieldMappingInvoice).find(
            key => fieldMappingInvoice[key].columnKey === columnKey
          );
          return backendField || null;
        })
        .filter((field): field is string => field !== null) // Filter out null values
        .join(','); // Join into a comma-separated string

      // Only update state if newLabeledFields is different
      setLabeledVisibleFields((prev: any) => {
        const isEqual = prev === newLabeledFields;
        if (!isEqual) {
          return newLabeledFields;
        }
        return prev;
      });
    },
    [fieldMappingInvoice]
  );

  const updateSearchParams = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value); // Set new value
    } else {
      newParams.delete(key); // Remove if empty
    }
    setSearchParams(newParams);
  };

  const navigateTo = (row: any) => {
    navigate(
      `/reports/purchase/invoice/purchase-invoice-result/${row?.original?.guid}`,
      {
        state: {
          name: row?.original?.invoice
        }
      }
    );
  };

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  // Initialize today's date in milliseconds
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const todayEnd = new Date().setHours(0, 0, 0, 0);

  // Use the provided dates or default to today's date
  const adjustedDateFrom = dateFrom || todayStart;
  /* prettier-ignore */
  const adjustedDateTo =
    !dateFrom && !dateTo
      ? todayEnd + 86400000
      : dateTo === dateFrom
        ? dateFrom + 86400000
        : dateTo
          ? dateTo + 86400000
          : undefined;

  const { data, isLoading, isFetching } = useQuery(
    [
      'GET_REPORTS',
      adjustedDateFrom,
      adjustedDateTo,
      paymentId,
      optionSenderId,
      currencyId,
      typeId,
      warehouseId,
      currentPage,
      filialId
    ],
    async () => {
      const res = await reportInvoiceService
        .getList({
          offset: pageToOffset(currentPage, 10),
          limit: 10,
          date_from: adjustedDateFrom,
          date_to: adjustedDateTo,
          supplier_pid: optionSenderId ? optionSenderId : undefined,
          payment_order_pid: paymentId ? paymentId : undefined,
          invoice_type_pid: typeId ? typeId : undefined,
          currency_pid: currencyId ? currencyId : undefined,
          warehouse_pid: warehouseId ? warehouseId : undefined,
          branch_pid: filialId ? filialId : undefined
        })
        .then((res: any) => res);
      return res;
    },
    {
      enabled:
        !!adjustedDateFrom ||
        !!adjustedDateTo ||
        !!paymentId ||
        !!optionSenderId ||
        !!currencyId ||
        !!typeId ||
        !!warehouseId ||
        !!currentPage ||
        !!filialId
    }
  );

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loading = isLoading || isFetching;

  /* prettier-ignore */
  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  const reportData: PurchaseData[] = useMemo(() => {
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    const resData =
      data?.invoices?.map((items: any) => {
        return {
          invoice: items?.invoice_number,
          /* prettier-ignore */
          invoice_date: formatDateToTimeZone(items?.invoice_date, timeZone),
          supplier: items?.supplier?.name,
          warehouse: items?.warehouse?.name,

          /* prettier-ignore */
          employee_opened_purchase:
           `${items?.employee?.first_name ?? ''} ${items?.employee?.last_name ?? ''}`.trim(),
          /* prettier-ignore */
          employee_closed_purchase:`${items?.closed_employee?.first_name ?? ''} ${items?.closed_employee?.last_name ?? ''}`.trim(),
          currency: items?.currency?.name?.[i18n?.language],
          type: items?.invoice_type?.name?.[i18n?.language],
          payment_procedure: items?.payment_order?.name?.[i18n?.language],
          branch: items?.legal_entity?.name,
          /* prettier-ignore */
          creation_date: formatDateToTimeZone(items?.date_created, timeZone),
          /* prettier-ignore */
          purchase_date:items?.maturity_date ? formatDateToTimeZone(items?.maturity_date, timeZone): '',
          guid: items?.id,
          gupid: items?.pid
        };
      }) ?? [];

    return resData;
  }, [data, dataSettings, t, i18n?.language]);

  const handleExcelById = (pid: number) => {
    const customFilename: string = 'Закуп по накладной.xlsx';

    // Fetch report and trigger download
    reportInvoiceService
      ?.getListExcel({ invoice_pid: pid })
      .then((res: any) => {
        if (res?.filename !== '') {
          const resUrl = `${url}${res?.filename}`;
          const link = document.createElement('a');
          link.href = resUrl;
          link.download = customFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      });
  };

  const purchaseDataTableColumns: ColumnDef<PurchaseData>[] = [
    {
      id: 'action2',
      cell: rowData => {
        return (
          <div>
            <Button
              variant="hover"
              onClick={(e: any) => {
                handleExcelById(rowData?.row?.original?.gupid);
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <ExcelIcon />
            </Button>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '5%', textAlign: 'center' } },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'order_number',
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1; // Calculate the order number based on the row index
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },

    {
      accessorKey: 'invoice',
      header: t('invoice_number'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'invoice_date',
      header: t('date_invoice'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'branch',
      header: t('branch'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'supplier',
      header: t('supplier'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'warehouse',
      header: t('warehouse'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'type',
      header: t('type'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'payment_procedure',
      header: t('payment_procedure'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'creation_date',
      header: t('date_create'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'purchase_date',
      header: t('maturity_date'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'employee_opened_purchase',
      header: t('employee_opened_purchase'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'employee_closed_purchase',
      header: t('employee_closed_purchase'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      id: 'action',
      header: t('price_tag'),
      cell: rowData => {
        return (
          <div>
            <Button
              variant="hover"
              onClick={(e: any) => {
                setSelectedInvoiceId(rowData?.row?.original?.guid);
                e.preventDefault();
                e.stopPropagation();
                setModalPriceList(true);
              }}
            >
              <FontAwesomeIcon
                className="cursor-pointer"
                icon={faTag}
                transform={{ size: 24 }}
              />
            </Button>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '5%', textAlign: 'center' } },
        cellProps: { className: 'text-center' }
      }
    }
  ];
  const table = useAdvanceTable({
    data: reportData,
    columns: purchaseDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <AdvanceTableProvider {...table}>
      <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
        <AdvanceTable
          tableProps={{ className: 'phoenix-table fs-9' }}
          navigateTo={navigateTo}
          isLoading={loading}
          onVisibleFieldsChange={handleVisibleFieldsChange}
        />
        <AdvanceTableCPagination
          count={pageCount}
          page={currentPage}
          setCurrentPage={setCurrentPage}
          onClick={(event, page) => {
            if (page) {
              updateSearchParams('pageParam', String(page));
            }
          }}
        />
      </div>

      <Modal
        show={modalPriceList}
        onHide={() => {
          setModalPriceList(false);
          setSelectedInvoiceId('');
        }}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {t('printing_price_tags')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PurchaseByInvoiceModalPriceList
            selectedInvoiceId={selectedInvoiceId}
          />
        </Modal.Body>
      </Modal>
    </AdvanceTableProvider>
  );
};

export default PurchaseByInvoiceTable;
